import { useEffect, useState } from "react";
import { api, dispDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Palmtree, CalendarRange, CheckCircle2 } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

// Get Sunday-Saturday range for a given date (defaults to today)
const getThisWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun..6=Sat
  const sun = new Date(now);
  sun.setDate(now.getDate() - day);
  const sat = new Date(sun);
  sat.setDate(sun.getDate() + 6);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(sun), end: fmt(sat) };
};

const emptyForm = { name: "", start_date: "", end_date: "" };

export default function AdminFreePeriods() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, a] = await Promise.all([
        api.get("/free-periods"),
        api.get("/free-periods/active"),
      ]);
      setList(l.data);
      setActive(a.data.period);
      setToday(a.data.today);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpenForm(true);
  };

  const quickThisWeek = () => {
    const { start, end } = getThisWeekRange();
    setEditing(null);
    setForm({ name: `Minggu Bebas ${dispDate(start)} - ${dispDate(end)}`, start_date: start, end_date: end });
    setOpenForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, start_date: p.start_date, end_date: p.end_date });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error("Semua field wajib diisi");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("Tanggal akhir tidak boleh sebelum tanggal mulai");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/free-periods/${editing.id}`, form);
        toast.success("Periode diperbarui");
      } else {
        await api.post("/free-periods", form);
        toast.success("Periode dibuat");
      }
      setOpenForm(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Hapus periode "${p.name}"?`)) return;
    try {
      await api.delete(`/free-periods/${p.id}`);
      toast.success("Periode dihapus");
      await refresh();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const getStatus = (p) => {
    if (today >= p.start_date && today <= p.end_date) return { label: "AKTIF", cls: "bg-amber-100 text-amber-800 border-amber-300" };
    if (today < p.start_date) return { label: "MENDATANG", cls: "bg-blue-100 text-blue-800 border-blue-300" };
    return { label: "SELESAI", cls: "bg-zinc-100 text-zinc-600 border-zinc-300" };
  };

  if (loading) return <PageLoader label="Memuat periode bebas..." />;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto" data-testid="admin-free-periods-page">
      <p className="overline">Pengaturan</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Periode Bebas</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Di dalam periode bebas: aturan telat & lembur karyawan bulanan dinonaktifkan. Karyawan PHL tidak diperbolehkan
        absen selama periode aktif.
      </p>

      {/* Today status */}
      <Card
        className={`p-5 mt-6 border shadow-none ${active ? "border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100" : "border-zinc-200"}`}
        data-testid="today-status-card"
      >
        <div className="flex items-center gap-3">
          {active ? (
            <Palmtree className="w-10 h-10 text-amber-700 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-10 h-10 text-emerald-600 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className="overline text-[0.6rem]">Status Hari Ini ({today})</p>
            {active ? (
              <>
                <p className="font-display text-xl font-bold text-amber-900" data-testid="today-status-label">
                  🌴 PERIODE BEBAS AKTIF
                </p>
                <p className="text-sm text-amber-800">{active.name} · {dispDate(active.start_date)} — {dispDate(active.end_date)}</p>
              </>
            ) : (
              <>
                <p className="font-display text-xl font-bold text-emerald-700" data-testid="today-status-label">
                  Normal
                </p>
                <p className="text-sm text-zinc-500">Aturan telat & lembur berlaku normal</p>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-6">
        <Button onClick={openNew} data-testid="btn-add-free-period">
          <Plus className="w-4 h-4 mr-1" /> Tambah Periode
        </Button>
        <Button variant="outline" onClick={quickThisWeek} data-testid="btn-quick-this-week">
          <CalendarRange className="w-4 h-4 mr-1" /> Aktifkan Minggu Ini
        </Button>
      </div>

      {/* List */}
      <div className="mt-4 space-y-2">
        {list.length === 0 && (
          <Card className="p-12 text-center border border-zinc-200 shadow-none">
            <p className="text-sm text-zinc-500">Belum ada periode bebas yang dibuat.</p>
          </Card>
        )}
        {list.map((p) => {
          const s = getStatus(p);
          return (
            <Card
              key={p.id}
              className="p-4 border border-zinc-200 shadow-none"
              data-testid={`free-period-row-${p.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{p.name}</p>
                    <Badge className={`${s.cls} text-[0.6rem] font-bold border`}>{s.label}</Badge>
                  </div>
                  <p className="text-sm text-zinc-500">
                    {dispDate(p.start_date)} — {dispDate(p.end_date)}
                    {" · "}
                    {(() => {
                      const sd = new Date(p.start_date);
                      const ed = new Date(p.end_date);
                      const days = Math.round((ed - sd) / 86400000) + 1;
                      return `${days} hari`;
                    })()}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p)} className="text-red-600" data-testid={`btn-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Form dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Periode Bebas" : "Tambah Periode Bebas"}</DialogTitle>
            <DialogDescription>Karyawan bulanan bebas telat & lembur. PHL diblok absen.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nama Periode *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="misal: Minggu Bebas HUT ke-15"
                required
                data-testid="input-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Mulai *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  required
                  data-testid="input-start-date"
                />
              </div>
              <div>
                <Label>Akhir *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  required
                  data-testid="input-end-date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)}>Batal</Button>
              <Button type="submit" disabled={busy} data-testid="btn-submit-period">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Update" : "Simpan")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
